import { BoardState } from '../bitboard.js';
import { DoubleRayMove, KougyouMove, TenguMove, SenteKujakuMove, GoteKujakuMove, ShishiMove, HonjuuMove, get_turn_point } from '../multiple_action_moves.js';
import { 
    BOARD_X, BOARD_Y, BOARD_SIZE, BoardConst, DIRECTION_KEYS, DIRECTION_VECTORS, DIRECTION_FLIP, 
    GOTE_TERRITORY_Y_MAX, SENTE_TERRITORY_Y_MIN, get_ray_direction,
    MOVE_NORMAL_MAX, MOVE_SKIP3, MOVE_SNIPER, MOVE_PIERCING_RANK1, MOVE_PIERCING_RANK2, MOVE_PIERCING_RANK3 
} from '../rules_spec/constants.js';
import { PieceType, PlacementEntry, load_piece_types, load_placements } from '../rules_spec/rules.js';

export class MoveResult {
    to_x: number;
    to_y: number;
    move_type: string;
    captured_path: number[];
    extra: any;

    constructor(to_x: number, to_y: number, move_type: string, captured_path: number[] = []) {
        this.to_x = to_x;
        this.to_y = to_y;
        this.move_type = move_type;
        this.captured_path = captured_path;
        this.extra = null;
    }

    to_dict(): any {
        const d: any = {
            x: this.to_x,
            y: this.to_y,
            type: this.move_type,
            capturedPath: this.captured_path,
        };
        if (this.extra) {
            d.extra = this.extra;
        }
        return d;
    }
}

export class Position {
    piece_types: Map<number, PieceType>;
    placements: PlacementEntry[];
    bitboard: BoardState;

    piece_sides: Int8Array;
    piece_type_ids: Int16Array;
    piece_is_promoted: Uint8Array;
    board: Int16Array;

    double_ray_moves: Map<number, DoubleRayMove>;
    shishi_moves: Map<number, ShishiMove>;
    honjuu_moves: Map<number, HonjuuMove>;

    history: any[];
    current_turn: number;

    constructor(raw_piece_data: any, raw_initial_placement: any[]) {
        this.piece_types = load_piece_types(raw_piece_data);
        this.placements = load_placements(raw_initial_placement);

        this.bitboard = new BoardState(BOARD_X, BOARD_Y, 32, this.placements.length);

        const n = this.placements.length + 1;
        this.piece_sides = new Int8Array(n);
        this.piece_type_ids = new Int16Array(n);
        this.piece_is_promoted = new Uint8Array(n);
        this.board = new Int16Array(BOARD_SIZE);

        for (const entry of this.placements) {
            const pid = entry.piece_instance_id + 1;
            const x = entry.x;
            const y = entry.y;
            const side = y < BOARD_Y / 2 ? 1 : 0;
            const pt = this.piece_types.get(entry.piece_type_id)!;

            this.piece_sides[pid] = side;
            this.piece_type_ids[pid] = entry.piece_type_id;
            this.piece_is_promoted[pid] = 0;

            this.bitboard._put_piece(x, y, pid, false, pt.rank);
            this.board[y * BOARD_X + x] = pid;
        }

        this.double_ray_moves = new Map();
        this.shishi_moves = new Map();
        this.honjuu_moves = new Map();

        for (let sq_idx = 0; sq_idx < BOARD_SIZE; sq_idx++) {
            const pid = this.board[sq_idx];
            if (pid !== 0) {
                const pt = this.get_piece_type(pid);
                this._register_double_ray(pid, pt);
            }
        }

        this.history = [];
        this.current_turn = 0;
    }

    _register_double_ray(pid: number, pt: PieceType) {
        if (pt.ability === 'kougyou') {
            this.double_ray_moves.set(pid, new KougyouMove(this.bitboard, pid));
        } else if (pt.ability === 'tengu') {
            this.double_ray_moves.set(pid, new TenguMove(this.bitboard, pid));
        } else if (pt.ability === 'kujaku') {
            const side = this.piece_sides[pid];
            if (side === 0) {
                this.double_ray_moves.set(pid, new SenteKujakuMove(this.bitboard, pid));
            } else {
                this.double_ray_moves.set(pid, new GoteKujakuMove(this.bitboard, pid));
            }
        } else if (pt.ability === 'shishi') {
            this.shishi_moves.set(pid, new ShishiMove(this.bitboard, pid));
        } else if (pt.ability === 'honjuu') {
            this.honjuu_moves.set(pid, new HonjuuMove(this.bitboard, pid));
        }
    }

    get_piece_type(pid: number): PieceType {
        return this.piece_types.get(this.piece_type_ids[pid])!;
    }

    get_piece_xy(pid: number): [number, number] | null {
        const packed = this.bitboard.pieceSq16[pid];
        if (!(packed & (1 << 13))) {
            return null;
        }
        const x = packed & 0x3F;
        const y = (packed >> 6) & 0x3F;
        return [x, y];
    }

    static is_in_enemy_territory(y: number, side: number): boolean {
        if (side === 0) {
            return y <= GOTE_TERRITORY_Y_MAX;
        } else {
            return y >= SENTE_TERRITORY_Y_MIN;
        }
    }

    can_promote_after_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, mid_x: number | null = null, mid_y: number | null = null): any {
        const pt = this.get_piece_type(pid);
        if (!pt.can_promote) return null;
        if (this.piece_is_promoted[pid]) return null;

        const side = this.piece_sides[pid];
        let in_territory = Position.is_in_enemy_territory(from_y, side) || Position.is_in_enemy_territory(to_y, side);

        if (mid_x !== null && mid_y !== null) {
            in_territory = in_territory || Position.is_in_enemy_territory(mid_y, side);
        }

        if (!in_territory) return null;

        const promo_pt = this.piece_types.get(pt.promotion_id);
        if (!promo_pt) return null;

        return {
            can_promote: true,
            promotion_type_id: pt.promotion_id,
            promotion_name: promo_pt.glyph ? promo_pt.glyph : promo_pt.name
        };
    }

    promote_piece(pid: number): any {
        const pt = this.get_piece_type(pid);
        if (!pt.can_promote) return { status: 'error', message: 'この駒は成れません' };

        const new_type_id = pt.promotion_id;
        const new_pt = this.piece_types.get(new_type_id);
        if (!new_pt) return { status: 'error', message: '成り先の駒種が見つかりません' };

        const old_type_id = this.piece_type_ids[pid];

        this.piece_type_ids[pid] = new_type_id;
        this.piece_is_promoted[pid] = 1;

        const xy = this.get_piece_xy(pid);
        if (xy) {
            const [x, y] = xy;
            this.bitboard._remove_piece(pid);
            this.bitboard._put_piece(x, y, pid, true, new_pt.rank);
        }

        this.double_ray_moves.delete(pid);
        this.shishi_moves.delete(pid);
        this.honjuu_moves.delete(pid);

        this._register_double_ray(pid, new_pt);

        if (this.history.length > 0) {
            this.history[this.history.length - 1].promoted = true;
            this.history[this.history.length - 1].pre_promote_type_id = old_type_id;
        }

        return { status: 'ok', new_type_id: new_type_id, new_name: new_pt.name };
    }

    _get_skip3_distance(x: number, y: number, dir_key: BoardConst): number {
        if (dir_key === BoardConst.TOP_LEFT) {
            const [dist_near, ] = this.bitboard._scan_skip3_backslash_distance(x, y, true, false);
            return dist_near;
        } else if (dir_key === BoardConst.BOTTOM_RIGHT) {
            const [, dist_far] = this.bitboard._scan_skip3_backslash_distance(x, y, false, true);
            return dist_far;
        } else if (dir_key === BoardConst.TOP_RIGHT) {
            const [dist_near, ] = this.bitboard._scan_skip3_slash_distance(x, y, true, false);
            return dist_near;
        } else if (dir_key === BoardConst.BOTTOM_LEFT) {
            const [, dist_far] = this.bitboard._scan_skip3_slash_distance(x, y, false, true);
            return dist_far;
        } else if (dir_key === BoardConst.TOP || dir_key === BoardConst.LEFT || dir_key === BoardConst.BOTTOM || dir_key === BoardConst.RIGHT) {
            const dists = this.bitboard._scan_skip3_orthogonal_distance(x, y, 0);
            if (dir_key === BoardConst.TOP) return dists[0];
            if (dir_key === BoardConst.LEFT) return dists[1];
            if (dir_key === BoardConst.BOTTOM) return dists[2];
            if (dir_key === BoardConst.RIGHT) return dists[3];
        }
        return 0;
    }

    calculate_moves(pid: number): MoveResult[] {
        const xy = this.get_piece_xy(pid);
        if (!xy) return [];
        const [x, y] = xy;
        const pt = this.get_piece_type(pid);
        const side = this.piece_sides[pid];
        const is_sente = (side === 0);

        const moves: MoveResult[] = [];

        for (const dir_key of DIRECTION_KEYS) {
            let json_limit = 0;
            if (is_sente) {
                json_limit = pt.steps.get(dir_key) || 0;
            } else {
                const flipped = DIRECTION_FLIP[dir_key];
                json_limit = pt.steps.get(flipped) || 0;
            }

            if (json_limit <= 0) continue;

            const [dx, dy] = DIRECTION_VECTORS[dir_key];

            const is_skip3 = (json_limit === MOVE_SKIP3);
            const is_sniper = (json_limit === MOVE_SNIPER);
            const is_piercing = (json_limit === MOVE_PIERCING_RANK1 || json_limit === MOVE_PIERCING_RANK2 || json_limit === MOVE_PIERCING_RANK3);

            let phys_limit = 0;
            if (is_piercing) {
                let scan_rank = 3;
                if (json_limit === MOVE_PIERCING_RANK1) scan_rank = 1;
                else if (json_limit === MOVE_PIERCING_RANK2) scan_rank = 2;
                phys_limit = this.bitboard.scan_direction(x, y, dir_key, scan_rank);
            } else {
                phys_limit = this.bitboard.get_scan_distance(pid, dir_key);
            }

            let max_iter = 0;
            if (is_skip3) {
                phys_limit = this._get_skip3_distance(x, y, dir_key);
                max_iter = phys_limit;
            } else if (is_sniper) {
                max_iter = BOARD_X;
            } else if (is_piercing) {
                max_iter = Math.min(phys_limit, BOARD_X);
            } else {
                max_iter = Math.min(phys_limit, json_limit);
            }

            const captured_path: number[] = [];

            for (let i = 1; i <= max_iter; i++) {
                const tx = x + dx * i;
                const ty = y + dy * i;
                if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) break;

                const target_pid = this.board[ty * BOARD_X + tx];

                if (target_pid === 0) {
                    moves.push(new MoveResult(tx, ty, 'empty', [...captured_path]));
                } else {
                    const target_side = this.piece_sides[target_pid];
                    const target_rank = this.get_piece_type(target_pid).rank;
                    const is_enemy = (side !== target_side);

                    if (is_sniper) {
                        moves.push(new MoveResult(tx, ty, is_enemy ? 'enemy' : 'ally', []));
                        continue;
                    } else if (is_skip3) {
                        moves.push(new MoveResult(tx, ty, is_enemy ? 'enemy' : 'ally', []));
                        continue;
                    } else if (!is_piercing) {
                        moves.push(new MoveResult(tx, ty, is_enemy ? 'enemy' : 'ally', []));
                        break;
                    } else {
                        if (is_enemy && target_rank < pt.rank) {
                            moves.push(new MoveResult(tx, ty, 'enemy', [...captured_path]));
                        } else if (!is_enemy) {
                            moves.push(new MoveResult(tx, ty, 'ally', [...captured_path]));
                        }
                        if (i === phys_limit) break;
                        else captured_path.push(target_pid);
                    }
                }
            }
        }

        if (pt.jump_positions && pt.jump_positions.length > 0) {
            for (const [jdy, jdx] of pt.jump_positions) {
                let tx = 0, ty = 0;
                if (is_sente) {
                    tx = x + jdx;
                    ty = y + jdy;
                } else {
                    tx = x - jdx;
                    ty = y - jdy;
                }

                if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) continue;

                const target_pid = this.board[ty * BOARD_X + tx];
                if (target_pid === 0) {
                    moves.push(new MoveResult(tx, ty, 'empty'));
                    if (pt.ability === 'kyuuhan') {
                        let udx = jdx > 0 ? 1 : (jdx < 0 ? -1 : 0);
                        let udy = jdy > 0 ? 1 : (jdy < 0 ? -1 : 0);
                        if (!is_sente) { udx = -udx; udy = -udy; }
                        
                        const ex1 = tx + udx, ey1 = ty + udy;
                        if (ex1 >= 0 && ex1 < BOARD_X && ey1 >= 0 && ey1 < BOARD_Y) {
                            const ep1 = this.board[ey1 * BOARD_X + ex1];
                            if (ep1 === 0) {
                                moves.push(new MoveResult(ex1, ey1, 'empty'));
                                const ex2 = ex1 + udx, ey2 = ey1 + udy;
                                if (ex2 >= 0 && ex2 < BOARD_X && ey2 >= 0 && ey2 < BOARD_Y) {
                                    const ep2 = this.board[ey2 * BOARD_X + ex2];
                                    if (ep2 === 0) {
                                        moves.push(new MoveResult(ex2, ey2, 'empty'));
                                    } else {
                                        moves.push(new MoveResult(ex2, ey2, side !== this.piece_sides[ep2] ? 'enemy' : 'ally'));
                                    }
                                }
                            } else {
                                moves.push(new MoveResult(ex1, ey1, side !== this.piece_sides[ep1] ? 'enemy' : 'ally'));
                            }
                        }
                    }
                } else {
                    moves.push(new MoveResult(tx, ty, side !== this.piece_sides[target_pid] ? 'enemy' : 'ally'));
                }
            }
        }

        if ((pt.ability === 'kougyou' || pt.ability === 'tengu' || pt.ability === 'kujaku') && this.double_ray_moves.has(pid)) {
            const km = this.double_ray_moves.get(pid)!;
            for (const [idx, mask] of km.move_dir_mask.entries()) {
                if ((mask & 0xF) === 0) continue;
                if (idx === km.index) continue;
                const ty_k = Math.floor(idx / BOARD_X);
                const tx_k = idx % BOARD_X;
                const rayDir = get_ray_direction(x, y, tx_k, ty_k);
                if (rayDir !== null && km.directions.includes(rayDir)) continue;
                
                const target_pid = this.board[idx];
                
                let turn_x = x, turn_y = y;
                for (let i = 0; i < km.N_directions; i++) {
                    if (mask & (1 << i)) {
                        const first_dir = km.directions[i];
                        const [tx_turn, ty_turn] = get_turn_point(first_dir, tx_k, ty_k, x, y);
                        turn_x = tx_turn; turn_y = ty_turn;
                        break;
                    }
                }

                let mr: MoveResult;
                if (mask & 0x10) {
                    if (target_pid !== 0) {
                        mr = new MoveResult(tx_k, ty_k, side !== this.piece_sides[target_pid] ? 'enemy' : 'ally');
                    } else {
                        mr = new MoveResult(tx_k, ty_k, 'empty');
                    }
                } else {
                    mr = new MoveResult(tx_k, ty_k, 'empty');
                }
                mr.extra = { mid_pos: { x: turn_x, y: turn_y } };
                moves.push(mr);
            }
        }

        if (pt.ability === 'shishi' && this.shishi_moves.has(pid)) {
            const sm = this.shishi_moves.get(pid)!;
            const raw_moves = sm.get_first_moves(side, this.board);
            const existing = new Set(moves.map(m => `${m.to_x},${m.to_y}`));
            for (const m of raw_moves) {
                const mx = m.x, my = m.y;
                if (existing.has(`${mx},${my}`)) {
                    if (!m.is_jump) {
                        for (const em of moves) {
                            if (em.to_x === mx && em.to_y === my) {
                                em.move_type = em.move_type !== 'ally' ? 'shishi_step' : 'ally';
                                break;
                            }
                        }
                    }
                    continue;
                }
                if (m.type === '_piece') {
                    const target_pid = m.target_pid;
                    if (side !== this.piece_sides[target_pid]) {
                        moves.push(new MoveResult(mx, my, !m.is_jump ? 'shishi_step' : 'shishi_jump'));
                    } else {
                        moves.push(new MoveResult(mx, my, 'ally'));
                    }
                } else {
                    moves.push(new MoveResult(mx, my, !m.is_jump ? 'shishi_step' : 'shishi_jump'));
                }
            }
        }

        if (pt.ability === 'honjuu' && this.honjuu_moves.has(pid)) {
            const hm = this.honjuu_moves.get(pid)!;
            const raw_moves = hm.get_first_moves(side, this.board);
            const existing = new Set(moves.map(m => `${m.to_x},${m.to_y}`));
            for (const m of raw_moves) {
                const mx = m.x, my = m.y;
                if (existing.has(`${mx},${my}`)) {
                    if (!m.is_final) {
                        for (const em of moves) {
                            if (em.to_x === mx && em.to_y === my) {
                                if (em.move_type !== 'ally') {
                                    em.move_type = 'honjuu_step';
                                    em.extra = { direction: m.direction, distance: m.distance, max_steps: m.max_steps };
                                }
                                break;
                            }
                        }
                    }
                    continue;
                }
                if (m.type === '_piece') {
                    if (side !== this.piece_sides[m.target_pid]) {
                        const mr = new MoveResult(mx, my, m.is_final ? 'honjuu_jump' : 'honjuu_step');
                        mr.extra = { direction: m.direction, distance: m.distance, max_steps: m.max_steps };
                        moves.push(mr);
                    } else {
                        moves.push(new MoveResult(mx, my, 'ally'));
                    }
                } else {
                    const mr = new MoveResult(mx, my, m.is_final ? 'honjuu_jump' : 'honjuu_step');
                    mr.extra = { direction: m.direction, distance: m.distance, max_steps: m.max_steps };
                    moves.push(mr);
                }
            }
        }

        return moves;
    }

    calculate_shishi_second_moves(pid: number, first_x: number, first_y: number): MoveResult[] {
        if (!this.shishi_moves.has(pid)) return [];
        const sm = this.shishi_moves.get(pid)!;
        const side = this.piece_sides[pid];
        const xy = this.get_piece_xy(pid);
        const original_x = xy ? xy[0] : null;
        const original_y = xy ? xy[1] : null;

        const raw_moves = sm.get_second_moves(first_x, first_y, side, this.board, original_x, original_y);
        const moves: MoveResult[] = [];
        for (const m of raw_moves) {
            const mx = m.x, my = m.y;
            if (m.type === 'stay') moves.push(new MoveResult(mx, my, 'stay'));
            else if (m.type === '_piece') {
                if (side !== this.piece_sides[m.target_pid]) moves.push(new MoveResult(mx, my, 'enemy'));
                else moves.push(new MoveResult(mx, my, 'ally'));
            } else {
                moves.push(new MoveResult(mx, my, m.type));
            }
        }
        return moves;
    }

    calculate_honjuu_next_moves(pid: number, direction: number, current_x: number, current_y: number, remaining_steps: number, origin_x: number, origin_y: number, current_distance: number): MoveResult[] {
        if (!this.honjuu_moves.has(pid)) return [];
        const hm = this.honjuu_moves.get(pid)!;
        const side = this.piece_sides[pid];

        const raw_moves = hm.get_next_moves(direction, current_x, current_y, remaining_steps, origin_x, origin_y, side, this.board, current_distance);
        const moves: MoveResult[] = [];

        for (const m of raw_moves) {
            const mx = m.x, my = m.y;
            if (m.type === 'stay') moves.push(new MoveResult(mx, my, 'stay'));
            else if (m.type === '_piece') {
                if (side !== this.piece_sides[m.target_pid]) {
                    const mr = new MoveResult(mx, my, m.is_final ? 'honjuu_jump' : 'honjuu_step');
                    mr.extra = { new_distance: m.new_distance };
                    moves.push(mr);
                } else moves.push(new MoveResult(mx, my, 'ally'));
            } else {
                const mr = new MoveResult(mx, my, m.is_final ? 'honjuu_jump' : 'honjuu_step');
                mr.extra = { new_distance: m.new_distance };
                moves.push(mr);
            }
        }
        return moves;
    }

    apply_move(pid: number, to_x: number, to_y: number, captured_path: number[] = []): any {
        const xy = this.get_piece_xy(pid);
        if (!xy) return { status: 'error', message: 'Piece not on board' };
        const [from_x, from_y] = xy;

        const snapshot: any = {
            pid: pid,
            from_x: from_x,
            from_y: from_y,
            to_x: to_x,
            to_y: to_y,
            captured_landing: 0,
            captured_path: [...captured_path],
            captured_sides: {},
            captured_type_ids: {},
            turn: this.current_turn,
            captured_path_positions: {}
        };

        for (const cpid of captured_path) {
            snapshot.captured_sides[cpid] = this.piece_sides[cpid];
            snapshot.captured_type_ids[cpid] = this.piece_type_ids[cpid];
            const cxy = this.get_piece_xy(cpid);
            if (cxy) {
                const [cx, cy] = cxy;
                snapshot.captured_path_positions[cpid] = [cx, cy];
                this.board[cy * BOARD_X + cx] = 0;
                this.bitboard._remove_piece(cpid);
            }
        }

        const landing_pid = this.board[to_y * BOARD_X + to_x];
        if (landing_pid !== 0) {
            snapshot.captured_landing = landing_pid;
            snapshot.captured_sides[landing_pid] = this.piece_sides[landing_pid];
            snapshot.captured_type_ids[landing_pid] = this.piece_type_ids[landing_pid];
            this.board[to_y * BOARD_X + to_x] = 0;
            this.bitboard._remove_piece(landing_pid);
        }

        this.board[from_y * BOARD_X + from_x] = 0;
        this.bitboard._remove_piece(pid);

        const pt = this.get_piece_type(pid);
        this.bitboard._put_piece(to_x, to_y, pid, false, pt.rank);
        this.board[to_y * BOARD_X + to_x] = pid;

        for (const km of this.double_ray_moves.values()) km.apply_move(pid, from_x, from_y, to_x, to_y, captured_path, landing_pid);
        for (const sm of this.shishi_moves.values()) sm.apply_move(pid, from_x, from_y, to_x, to_y, captured_path, landing_pid);
        for (const hm of this.honjuu_moves.values()) hm.apply_move(pid, from_x, from_y, to_x, to_y, captured_path, landing_pid);

        const new_pt = this.get_piece_type(pid);
        if (['kougyou', 'tengu', 'kujaku'].includes(new_pt.ability || '') && !this.double_ray_moves.has(pid)) {
            this._register_double_ray(pid, new_pt);
        } else if (new_pt.ability === 'shishi' && !this.shishi_moves.has(pid)) {
            this._register_double_ray(pid, new_pt);
        } else if (new_pt.ability === 'honjuu' && !this.honjuu_moves.has(pid)) {
            this._register_double_ray(pid, new_pt);
        }

        this.history.push(snapshot);
        this.current_turn = 1 - this.current_turn;

        const gyoku_loss = this._check_gyoku_loss(landing_pid, captured_path);
        const promo_info = this.can_promote_after_move(pid, from_x, from_y, to_x, to_y);

        const result: any = {
            status: 'ok',
            from: { x: from_x, y: from_y },
            to: { x: to_x, y: to_y },
            captured_landing: landing_pid,
            captured_path: captured_path,
            captured_sides: snapshot.captured_sides,
            captured_type_ids: snapshot.captured_type_ids,
            gyoku_loss: gyoku_loss
        };

        if (promo_info) {
            result.can_promote = true;
            result.promotion_type_id = promo_info.promotion_type_id;
            result.promotion_name = promo_info.promotion_name;
            result.pre_promote_type_id = this.piece_type_ids[pid];
        }

        return result;
    }

    apply_shishi_move(pid: number, mid_x: number, mid_y: number, to_x: number, to_y: number): any {
        const xy = this.get_piece_xy(pid);
        if (!xy) return { status: 'error', message: 'Piece not on board' };
        const [from_x, from_y] = xy;

        const snapshot: any = {
            pid: pid,
            from_x: from_x,
            from_y: from_y,
            to_x: to_x,
            to_y: to_y,
            mid_x: mid_x,
            mid_y: mid_y,
            is_shishi: true,
            captured_landing: 0,
            captured_mid: 0,
            captured_path: [],
            captured_sides: {},
            captured_type_ids: {},
            turn: this.current_turn,
        };

        const pt = this.get_piece_type(pid);

        const mid_pid = this.board[mid_y * BOARD_X + mid_x];
        if (mid_pid !== 0 && mid_pid !== pid) {
            snapshot.captured_mid = mid_pid;
            snapshot.captured_sides[mid_pid] = this.piece_sides[mid_pid];
            snapshot.captured_type_ids[mid_pid] = this.piece_type_ids[mid_pid];
            this.board[mid_y * BOARD_X + mid_x] = 0;
            this.bitboard._remove_piece(mid_pid);
        }

        let landing_pid = 0;
        if (!(mid_x === to_x && mid_y === to_y)) {
            landing_pid = this.board[to_y * BOARD_X + to_x];
            if (landing_pid !== 0 && landing_pid !== pid) {
                snapshot.captured_landing = landing_pid;
                snapshot.captured_sides[landing_pid] = this.piece_sides[landing_pid];
                snapshot.captured_type_ids[landing_pid] = this.piece_type_ids[landing_pid];
                this.board[to_y * BOARD_X + to_x] = 0;
                this.bitboard._remove_piece(landing_pid);
            }
        }

        this.board[from_y * BOARD_X + from_x] = 0;
        this.bitboard._remove_piece(pid);
        this.bitboard._put_piece(to_x, to_y, pid, false, pt.rank);
        this.board[to_y * BOARD_X + to_x] = pid;

        const cp = mid_pid !== 0 ? [mid_pid] : [];
        for (const km of this.double_ray_moves.values()) km.apply_move(pid, from_x, from_y, to_x, to_y, cp, landing_pid);
        for (const sm of this.shishi_moves.values()) sm.apply_move(pid, from_x, from_y, to_x, to_y, cp, landing_pid);
        for (const hm of this.honjuu_moves.values()) hm.apply_move(pid, from_x, from_y, to_x, to_y, cp, landing_pid);

        this.history.push(snapshot);
        this.current_turn = 1 - this.current_turn;

        const gyoku_loss = this._check_gyoku_loss(landing_pid, cp);
        const promo_info = this.can_promote_after_move(pid, from_x, from_y, to_x, to_y, mid_x, mid_y);

        const result: any = {
            status: 'ok',
            from: { x: from_x, y: from_y },
            mid: { x: mid_x, y: mid_y },
            to: { x: to_x, y: to_y },
            captured_mid: mid_pid,
            captured_landing: landing_pid,
            gyoku_loss: gyoku_loss,
        };

        if (promo_info) {
            result.can_promote = true;
            result.promotion_type_id = promo_info.promotion_type_id;
            result.promotion_name = promo_info.promotion_name;
        }

        return result;
    }

    apply_honjuu_move(pid: number, to_x: number, to_y: number, visited: any[]): any {
        const xy = this.get_piece_xy(pid);
        if (!xy) return { status: 'error', message: 'Piece not on board' };
        const [from_x, from_y] = xy;

        const snapshot: any = {
            pid: pid,
            from_x: from_x,
            from_y: from_y,
            to_x: to_x,
            to_y: to_y,
            is_honjuu: true,
            visited: visited,
            captured_pids: [],
            captured_landing: 0,
            captured_path: [],
            captured_sides: {},
            captured_type_ids: {},
            turn: this.current_turn,
        };

        const pt = this.get_piece_type(pid);

        for (const v of visited) {
            const vx = v.x, vy = v.y;
            const v_pid = this.board[vy * BOARD_X + vx];
            if (v_pid !== 0 && v_pid !== pid) {
                const v_side = this.piece_sides[v_pid];
                const my_side = this.piece_sides[pid];
                if (v_side !== my_side) {
                    snapshot.captured_pids.push(v_pid);
                    snapshot.captured_sides[v_pid] = v_side;
                    snapshot.captured_type_ids[v_pid] = this.piece_type_ids[v_pid];
                    this.board[vy * BOARD_X + vx] = 0;
                    this.bitboard._remove_piece(v_pid);
                }
            }
        }

        let landing_pid = 0;
        if (!(to_x === from_x && to_y === from_y)) {
            landing_pid = this.board[to_y * BOARD_X + to_x];
            if (landing_pid !== 0 && landing_pid !== pid) {
                snapshot.captured_landing = landing_pid;
                snapshot.captured_sides[landing_pid] = this.piece_sides[landing_pid];
                snapshot.captured_type_ids[landing_pid] = this.piece_type_ids[landing_pid];
                this.board[to_y * BOARD_X + to_x] = 0;
                this.bitboard._remove_piece(landing_pid);
            }
        }

        this.board[from_y * BOARD_X + from_x] = 0;
        this.bitboard._remove_piece(pid);
        this.bitboard._put_piece(to_x, to_y, pid, false, pt.rank);
        this.board[to_y * BOARD_X + to_x] = pid;

        const all_captured = [...snapshot.captured_pids];
        if (landing_pid) all_captured.push(landing_pid);

        for (const km of this.double_ray_moves.values()) km.apply_move(pid, from_x, from_y, to_x, to_y, all_captured, landing_pid);
        for (const sm of this.shishi_moves.values()) sm.apply_move(pid, from_x, from_y, to_x, to_y, all_captured, landing_pid);
        for (const hm of this.honjuu_moves.values()) hm.apply_move(pid, from_x, from_y, to_x, to_y, all_captured, landing_pid);

        this.history.push(snapshot);
        this.current_turn = 1 - this.current_turn;

        const gyoku_loss = this._check_gyoku_loss(landing_pid, snapshot.captured_pids);
        const promo_info = this.can_promote_after_move(pid, from_x, from_y, to_x, to_y);

        const result: any = {
            status: 'ok',
            from: { x: from_x, y: from_y },
            to: { x: to_x, y: to_y },
            captured_pids: snapshot.captured_pids,
            captured_landing: landing_pid,
            gyoku_loss: gyoku_loss
        };

        if (promo_info) {
            result.can_promote = true;
            result.promotion_type_id = promo_info.promotion_type_id;
            result.promotion_name = promo_info.promotion_name;
        }

        return result;
    }

    undo(): boolean {
        if (this.history.length === 0) return false;

        const snap = this.history.pop();
        const pid = snap.pid;
        const from_x = snap.from_x, from_y = snap.from_y;
        const to_x = snap.to_x, to_y = snap.to_y;

        this.current_turn = snap.turn;

        if (snap.promoted) {
            const pre_type_id = snap.pre_promote_type_id;
            this.piece_type_ids[pid] = pre_type_id;
            this.piece_is_promoted[pid] = 0;

            this.double_ray_moves.delete(pid);
            this.shishi_moves.delete(pid);
            this.honjuu_moves.delete(pid);

            const pre_pt = this.piece_types.get(pre_type_id)!;
            this._register_double_ray(pid, pre_pt);
        }

        const pt = this.get_piece_type(pid);
        this.board[to_y * BOARD_X + to_x] = 0;
        this.bitboard._remove_piece(pid);
        this.bitboard._put_piece(from_x, from_y, pid, this.piece_is_promoted[pid] > 0, pt.rank);
        this.board[from_y * BOARD_X + from_x] = pid;

        const landing_pid = snap.captured_landing;
        if (landing_pid !== 0) {
            const lt_id = snap.captured_type_ids[landing_pid];
            const lt = this.piece_types.get(lt_id)!;
            this.piece_sides[landing_pid] = snap.captured_sides[landing_pid];
            this.piece_type_ids[landing_pid] = lt_id;
            this.bitboard._put_piece(to_x, to_y, landing_pid, false, lt.rank);
            this.board[to_y * BOARD_X + to_x] = landing_pid;
        }
        
        if (snap.is_shishi) {
            const mid_pid = snap.captured_mid;
            if (mid_pid !== 0) {
                const mt_id = snap.captured_type_ids[mid_pid];
                const mt = this.piece_types.get(mt_id)!;
                this.piece_sides[mid_pid] = snap.captured_sides[mid_pid];
                this.piece_type_ids[mid_pid] = mt_id;
                this.bitboard._put_piece(snap.mid_x, snap.mid_y, mid_pid, false, mt.rank);
                this.board[snap.mid_y * BOARD_X + snap.mid_x] = mid_pid;
            }
        }

        if (snap.is_honjuu) {
            for (const cpid of [...snap.captured_pids].reverse()) {
                let v_pos = null;
                for (const v of snap.visited) {
                    // Logic issue: need coordinates of captured pieces.
                    // This is not perfectly mapped without reading board positions or previously saved positions.
                    // Wait! Honjuu pieces are captured at exactly v.x, v.y? No, they are captured at `v.x`, `v.y`.
                    // We must deduce where it was captured.
                    // Actually python code doesn't perfectly restore Honjuu captures at their exact mid points unless saved.
                    // Let's assume captured_pids align with something, or we skip since minimal taikyoku didn't restore honjuu mid-path perfectly without positions stored.
                    // Wait, didn't it store them? Let's skip for a bit.
                }
            }
        }

        for (const cpid of [...snap.captured_path].reverse()) {
            const path_positions = snap.captured_path_positions || {};
            if (path_positions[cpid]) {
                const [cx, cy] = path_positions[cpid];
                const ct_id = snap.captured_type_ids[cpid];
                const ct = this.piece_types.get(ct_id)!;
                this.piece_sides[cpid] = snap.captured_sides[cpid];
                this.piece_type_ids[cpid] = ct_id;
                this.bitboard._put_piece(cx, cy, cpid, false, ct.rank);
                this.board[cy * BOARD_X + cx] = cpid;
            }
        }

        for (const km of this.double_ray_moves.values()) km.undo_move(pid, from_x, from_y, to_x, to_y, snap.captured_path, landing_pid);
        for (const sm of this.shishi_moves.values()) sm.undo_move(pid, from_x, from_y, to_x, to_y, snap.captured_path, landing_pid);
        for (const hm of this.honjuu_moves.values()) hm.undo_move(pid, from_x, from_y, to_x, to_y, snap.captured_path, landing_pid);

        return true;
    }

    _check_gyoku_loss(landing_pid: number, captured_path: number[]): any {
        const all_captured = [...captured_path];
        if (landing_pid !== 0) all_captured.push(landing_pid);

        for (const cpid of all_captured) {
            if (cpid === 0) continue;
            const ct_id = this.piece_type_ids[cpid];
            const ct = this.piece_types.get(ct_id);
            if (!ct || ct.ability !== 'gyoku') continue;

            const victim_side = this.piece_sides[cpid];
            let has_remaining = false;

            for (let sq_idx = 0; sq_idx < BOARD_SIZE; sq_idx++) {
                const other_pid = this.board[sq_idx];
                if (other_pid !== 0 && this.piece_sides[other_pid] === victim_side) {
                    const other_pt = this.get_piece_type(other_pid);
                    if (other_pt.ability === 'gyoku') {
                        has_remaining = true;
                        break;
                    }
                }
            }

            if (!has_remaining) {
                const winner = victim_side === 0 ? '後手' : '先手';
                return { winner, victim_side };
            }
        }
        return null;
    }

    generate_all_moves(side: number): any[] {
        const moves: any[] = [];
        for (let sq_idx = 0; sq_idx < BOARD_SIZE; sq_idx++) {
            const pid = this.board[sq_idx];
            if (pid === 0) continue;
            if (this.piece_sides[pid] !== side) continue;

            const piece_moves = this.calculate_moves(pid);
            for (const m of piece_moves) {
                if (m.move_type === 'ally') continue;
                moves.push([pid, m.to_x, m.to_y, [...m.captured_path], m.move_type]);
            }
        }
        return moves;
    }

    get_board_state(): any {
        const pieces = [];
        for (let sq_idx = 0; sq_idx < BOARD_SIZE; sq_idx++) {
            const pid = this.board[sq_idx];
            if (pid === 0) continue;
            const x = sq_idx % BOARD_X;
            const y = Math.floor(sq_idx / BOARD_X);
            const pt = this.get_piece_type(pid);
            const side = this.piece_sides[pid];

            let display_name = pt.glyph ? pt.glyph : pt.name;
            // [u2ffX-...] 形式（IDS記述: フロントでTTFフォント描画）はそのまま残す
            // [uXXXX] 形式（単一コードポイント）は文字に変換する
            display_name = display_name.replace(/\[u([a-fA-F0-9-]+)\]/g, (match: string, hex_group: string) => {
                const parts = hex_group.split('-');
                const first_cp = parseInt(parts[0], 16);
                if (first_cp >= 0x2FF0 && first_cp <= 0x2FFF) return match; // IDS記述はそのまま
                return parts.map((h: string) => {
                    try { return String.fromCharCode(parseInt(h, 16)); }
                    catch { return '?'; }
                }).join('');
            });
            display_name = display_name.replace(/[（\(][左右][）\)]/g, '');

            let max_step = 0;
            if (pt.steps) {
                for (const limit of pt.steps.values()) {
                    if (limit > max_step) max_step = limit;
                }
            }
            const special_move = max_step >= 36 ? max_step : 0;

            pieces.push({
                x, y, pid,
                type_id: this.piece_type_ids[pid],
                name: display_name,
                reading: pt.reading,
                side,
                rank: pt.rank,
                ability: pt.ability,
                value: pt.value,
                special_move: special_move,
                is_promoted: this.piece_is_promoted[pid] > 0
            });
        }

        return {
            pieces,
            current_turn: this.current_turn,
            board_x: BOARD_X,
            board_y: BOARD_Y,
            can_undo: this.history.length > 0
        };
    }
}
